import {
  Box,
  Button,
  DateFormats,
  DateInput,
  Dropdown,
  formatJSDate,
  Inline,
  type OptionType,
  type SingleValue,
  Switcher,
  useViewport,
} from '@omnipresentgroup/design-system';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  CATEGORY_PARAM,
  DATE_FROM_PARAMETER,
  DATE_TO_PARAMETER,
  EMPLOYEE_ID_PARAM,
  ManagerEmployeeSelector,
  STATUS_PARAM,
} from '@omnipresentgroup/shared';
import {
  type BaseExpense,
  ExpenseCategoryEnum,
  ExpenseStatusEnum,
} from '@omnipresentgroup/types';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

export const useExpensesFilters = ({
  expenses,
  clientId,
  enabled = false,
}: {
  expenses: BaseExpense[];
  clientId?: string;
  enabled?: boolean;
}) => {
  const { isPhone, isTablet } = useViewport();
  const [searchParameters, setSearchParameters] = useSearchParams();

  const employeeIdParameter = new URLSearchParams(searchParameters).get(
    EMPLOYEE_ID_PARAM,
  ) as string;

  const statusParameter = new URLSearchParams(searchParameters).get(
    STATUS_PARAM,
  ) as string;

  const categoryParameter = new URLSearchParams(searchParameters).get(
    CATEGORY_PARAM,
  ) as string;

  const dateFromParameter = new URLSearchParams(searchParameters).get(
    DATE_FROM_PARAMETER,
  ) as string;

  const dateToParameter = new URLSearchParams(searchParameters).get(
    DATE_TO_PARAMETER,
  ) as string;

  const filteredExpenses = useMemo(() => {
    return enabled
      ? expenses?.filter((expense) => {
          const matchesEmployeeId = employeeIdParameter
            ? expense.employeeId === employeeIdParameter
            : true;

          const matchesStatus = statusParameter
            ? // eslint-disable-next-line unicorn/no-nested-ternary
              statusParameter === ExpenseStatusEnum.PENDING // When the status is pending, we need to check if the expense is pending or change made
              ? [
                  ExpenseStatusEnum.PENDING,
                  ExpenseStatusEnum.CHANGE_MADE,
                ].includes(expense.status)
              : expense.status === statusParameter
            : true;

          const matchesCategory = categoryParameter
            ? expense.category === categoryParameter
            : true;

          const matchesDateFrom = dateFromParameter
            ? DateTime.fromISO(expense.dateSpent) >=
              DateTime.fromISO(dateFromParameter)
            : true;

          const matchesDateTo = dateToParameter
            ? DateTime.fromISO(expense.dateSpent) <=
              DateTime.fromISO(dateToParameter)
            : true;

          return (
            matchesEmployeeId &&
            matchesStatus &&
            matchesCategory &&
            matchesDateFrom &&
            matchesDateTo
          );
        })
      : expenses;
  }, [
    enabled,
    expenses,
    employeeIdParameter,
    statusParameter,
    categoryParameter,
    dateFromParameter,
    dateToParameter,
  ]);

  const isStacked = isPhone || isTablet;

  const Filters = () => {
    return enabled ? (
      <Switcher isStacked={isStacked} css={{ flexWrap: 'wrap' }}>
        {clientId && (
          <Box minW={isStacked ? undefined : 200}>
            <ManagerEmployeeSelector label="Employee" companyId={clientId} />
          </Box>
        )}
        <Box minW={isStacked ? undefined : 200}>
          <Dropdown
            isMulti={false}
            size="medium"
            id="expense-status"
            label="Status"
            placeholder="Select status"
            onChange={(option, actionMeta) => {
              if (actionMeta.action === 'clear') {
                setSearchParameters((parameters) => {
                  parameters.delete(STATUS_PARAM);
                  return parameters;
                });
              } else {
                setSearchParameters((parameters) => {
                  parameters.set(
                    STATUS_PARAM,
                    (option as SingleValue<OptionType>)?.value as string,
                  );
                  return parameters;
                });
              }
            }}
            options={[
              {
                options: Object.values(ExpenseStatusEnum)
                  .filter((status) => status !== ExpenseStatusEnum.CHANGE_MADE) // Discarding the change made status because it is a pending status
                  .filter((status) => status !== ExpenseStatusEnum.PROCESSED) // TODO. Remove this when the reported status is implemented
                  .map((status) => ({
                    label: toSentenceCase(status),
                    value: status,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              },
            ]}
            isSearchable
            isClearable
            value={
              statusParameter
                ? {
                    label: toSentenceCase(statusParameter),
                    value: statusParameter,
                  }
                : null
            }
          />
        </Box>
        <Box minW={isStacked ? undefined : 250}>
          <DateInput
            id="expense-date-range"
            selectsRange={true}
            label="Date range"
            placeholderText="Select date range"
            startDate={
              dateFromParameter
                ? DateTime.fromISO(dateFromParameter, {
                    zone: 'utc',
                  }).toJSDate()
                : null
            }
            endDate={
              dateToParameter
                ? DateTime.fromISO(dateToParameter, {
                    zone: 'utc',
                  }).toJSDate()
                : null
            }
            onChange={(update) => {
              if (!update[0]) {
                setSearchParameters((parameters) => {
                  parameters.delete(DATE_FROM_PARAMETER);
                  return parameters;
                });
              }
              if (!update[1]) {
                setSearchParameters((parameters) => {
                  parameters.delete(DATE_TO_PARAMETER);
                  return parameters;
                });
              }
              if (update[0]) {
                const dateFrom = formatJSDate(update[0], DateFormats.FixedDate);
                setSearchParameters((parameters) => {
                  parameters.set(DATE_FROM_PARAMETER, dateFrom);
                  return parameters;
                });
              }
              if (update[1]) {
                const dateTo = formatJSDate(update[1], DateFormats.FixedDate);
                setSearchParameters((parameters) => {
                  parameters.set(DATE_TO_PARAMETER, dateTo);
                  return parameters;
                });
              }
            }}
            monthsShown={1}
            popperModifiers={[
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                },
                fn: (state) => state,
              },
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['top-start', 'bottom-end'],
                },
                fn: (state) => state,
              },
            ]}
          />
        </Box>
        <Box minW={isStacked ? undefined : 200}>
          <Dropdown
            isMulti={false}
            size="medium"
            id="expense-category"
            label="Category"
            placeholder="Select category"
            onChange={(option, actionMeta) => {
              if (actionMeta.action === 'clear') {
                setSearchParameters((parameters) => {
                  parameters.delete(CATEGORY_PARAM);
                  return parameters;
                });
              } else {
                setSearchParameters((parameters) => {
                  parameters.set(
                    CATEGORY_PARAM,
                    (option as SingleValue<OptionType>)?.value as string,
                  );
                  return parameters;
                });
              }
            }}
            options={[
              {
                options: Object.values(ExpenseCategoryEnum)
                  .map((category) => ({
                    label: toSentenceCase(category) ?? '',
                    value: category,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              },
            ]}
            isSearchable
            isClearable
            value={
              categoryParameter
                ? {
                    label: toSentenceCase(categoryParameter),
                    value: categoryParameter,
                  }
                : null
            }
          />
        </Box>
        <Inline justify="end" align="end">
          <Button
            variant="tertiary"
            icon="FilterOff"
            onClick={() => {
              setSearchParameters((parameters) => {
                parameters.delete(EMPLOYEE_ID_PARAM);
                parameters.delete(STATUS_PARAM);
                parameters.delete(CATEGORY_PARAM);
                parameters.delete(DATE_FROM_PARAMETER);
                parameters.delete(DATE_TO_PARAMETER);
                return parameters;
              });
            }}
          >
            Clear
          </Button>
        </Inline>
      </Switcher>
    ) : null;
  };

  return {
    data: filteredExpenses,
    Filters,
  };
};
